import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";
import type { CanvasNode, JsonValue } from "../domain/types";
import { useCanvasMessages } from "./i18n";

function textProp(
  props: Record<string, JsonValue>,
  key: string,
): string | undefined {
  return typeof props[key] === "string" ? props[key] : undefined;
}

export function canvasNodeAccessibleName(node: CanvasNode): string {
  return (
    textProp(node.props, "title") ??
    textProp(node.props, "text") ??
    textProp(node.props, "label") ??
    textProp(node.props, "alt") ??
    (node.reference === undefined
      ? undefined
      : `${node.reference.kind}:${node.reference.id}`) ??
    `${node.type} ${node.id}`
  );
}

export interface CanvasOutlineProps {
  editor: CanvasEditor;
  snapshot: CanvasEditorSnapshot;
  onNodeActivate?(nodeId: CanvasNode["id"]): void;
}

export function CanvasOutline({
  editor,
  snapshot,
  onNodeActivate,
}: CanvasOutlineProps) {
  const messages = useCanvasMessages();
  const nodes = [...snapshot.canvas.nodes].sort((left, right) => {
    const leftOrder = left.readingOrder ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right.readingOrder ?? Number.MAX_SAFE_INTEGER;
    return (
      leftOrder - rightOrder ||
      left.geometry.y - right.geometry.y ||
      left.geometry.x - right.geometry.x
    );
  });
  return (
    <aside
      aria-label={messages.outlineLabel}
      style={{
        position: "absolute",
        zIndex: 18,
        top: 70,
        right: 12,
        width: 280,
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100% - 140px)",
        overflow: "auto",
        padding: 10,
        border: "1px solid #dbe3ee",
        borderRadius: 12,
        background: "rgba(255,255,255,.97)",
        boxShadow: "0 6px 24px rgba(15,23,42,.12)",
      }}
    >
      <strong style={{ display: "block", padding: "4px 6px 8px" }}>
        {messages.outlineLabel}
      </strong>
      <span
        aria-live="polite"
        style={{ display: "block", padding: "0 6px 8px", color: "#64748b" }}
      >
        {messages.nodesCount(nodes.length)}
      </span>
      <ol style={{ margin: 0, paddingInlineStart: 24 }}>
        {nodes.map((node) => {
          const selected = snapshot.selection.nodeIds.has(node.id);
          return (
            <li key={node.id}>
              <button
                type="button"
                aria-current={selected ? "true" : undefined}
                onClick={() => {
                  editor.setSelection({
                    nodeIds: [node.id],
                    primaryId: node.id,
                  });
                  const centerX = node.geometry.x + node.geometry.width / 2;
                  const centerY = node.geometry.y + node.geometry.height / 2;
                  editor.panBy(
                    snapshot.view.viewportWidth / 2 -
                      snapshot.view.panX -
                      centerX * snapshot.view.zoom,
                    snapshot.view.viewportHeight / 2 -
                      snapshot.view.panY -
                      centerY * snapshot.view.zoom,
                  );
                  onNodeActivate?.(node.id);
                }}
                style={{
                  width: "100%",
                  padding: "7px 8px",
                  border: 0,
                  borderRadius: 6,
                  color: "#172033",
                  background: selected ? "#e0e7ff" : "transparent",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                {canvasNodeAccessibleName(node)}
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}
